import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Contact2, UserCheck, Users, BarChart3, FileSpreadsheet,
  Palette, Settings, LogOut, PanelLeftClose, PanelLeftOpen, FileSearch,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["master_admin", "admin", "lead_generator", "sales_executive"] },
  { to: "/leads", label: "Leads", icon: Contact2, roles: ["master_admin", "admin", "lead_generator", "sales_executive"] },
  { to: "/customers", label: "Customers", icon: UserCheck, roles: ["master_admin", "admin", "sales_executive"] },
  { to: "/analytics", label: "Analytics", icon: BarChart3, roles: ["master_admin"] },
  { to: "/reports", label: "Reports", icon: FileSpreadsheet, roles: ["master_admin"] },
  { to: "/access-log", label: "Access Log", icon: FileSearch, roles: ["master_admin"] },
  { to: "/users", label: "Users", icon: Users, roles: ["master_admin", "admin"] },
  { to: "/branding", label: "Branding", icon: Palette, roles: ["master_admin"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["master_admin"] },
];

export function Sidebar() {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  if (!user) return null;
  const items = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "flex flex-col border-r border-border h-screen sticky top-0 transition-[width] duration-200",
        collapsed ? "w-[64px]" : "w-[240px]"
      )}
      style={{
        backgroundColor: "color-mix(in srgb, var(--sidebar-color, transparent) 8%, hsl(var(--background)))",
      }}
    >
      <div className="h-14 flex items-center px-3 border-b border-border">
        <button onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-left flex-1 min-w-0" data-testid="sidebar-brand">
          <div className="w-7 h-7 rounded-sm flex items-center justify-center text-white text-xs font-semibold shrink-0"
            style={{ backgroundColor: "var(--theme-color)" }}>
            {branding.company_name?.charAt(0) || "C"}
          </div>
          {!collapsed && <span className="text-sm font-semibold tracking-tight truncate">{branding.company_name}</span>}
        </button>
      </div>

      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink key={item.to} to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) => cn(
                "flex items-center gap-2.5 px-2.5 py-2 rounded-sm text-sm transition-colors",
                isActive
                  ? "bg-secondary text-foreground font-medium"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              )}>
              <Icon className="w-4 h-4 shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-border p-2 space-y-0.5">
        <button onClick={() => setCollapsed((c) => !c)} data-testid="sidebar-collapse-toggle"
          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-sm text-sm text-muted-foreground hover:bg-secondary hover:text-foreground">
          {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          {!collapsed && <span>Collapse</span>}
        </button>
        <button onClick={logout} data-testid="logout-btn"
          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-sm text-sm text-muted-foreground hover:bg-secondary hover:text-foreground">
          <LogOut className="w-4 h-4" />
          {!collapsed && <span>Log out</span>}
        </button>
      </div>
    </aside>
  );
}
